import React, {useState, useRef, useCallback, useEffect} from 'react';
import {Button, Text, Icon, WithTooltip} from '@albert-io/atomic';

import useOnClickOutside from 'lib/hooks/useOnClickOutside';
import classnames from 'classnames';
import {useShallow} from 'zustand/react/shallow';
import {
  addMediaText,
  useQuestionTranslationStore
} from 'client/components/QuestionTypes/shared/TranslationSection';

import QuestionTypeStore from '../../../../../../QuestionType.store';

import {ImageUploadButton} from './UploadButtons/ImageUploadButton.react';
import {AudioUploadButton} from './UploadButtons/AudioUploadButton.react';

interface AddMediaButtonProps {
  tabIndex?: number;
}

export const AddMediaButton = ({tabIndex}: AddMediaButtonProps) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [activeCursor, setActiveCursor] = useState<number>(0);

  // Media uploads require a draft guess, which shouldNotSaveDraft() already
  // captures the conditions for (no assignment, e.g. standalone FRQ practice;
  // not a student; no session). Disable uploads instead of 403ing silently.
  const isMediaUploadSupported = !QuestionTypeStore.shouldNotSaveDraft();

  const {showTranslations} = useQuestionTranslationStore(
    useShallow((state) => ({
      showTranslations: state.showTranslations && state.translationSettings.question
    }))
  );

  const handleClickOutside = useCallback(() => {
    if (showDropdown) {
      setShowDropdown(false);
    }
  }, [showDropdown]);

  useOnClickOutside(buttonRef, handleClickOutside);

  const keyHandlers = {
    ArrowDown: () => setActiveCursor((current) => (current === 0 ? 1 : 0)),
    ArrowUp: () => setActiveCursor((current) => (current === 0 ? 1 : 0)),
    ArrowRight: () => setShowDropdown(false),
    ArrowLeft: () => setShowDropdown(false),
    Escape: () => setShowDropdown(false),
    Enter: () => {
      const selected = dropdownRef.current!.children[activeCursor] as HTMLElement;
      if (selected) {
        selected.click();
      }
    }
  };

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (showDropdown && dropdownRef.current) {
        event.preventDefault();
        const handler = keyHandlers[event.key];
        handler?.();
      }
    },
    [activeCursor]
  );

  useEffect(() => {
    if (showDropdown && dropdownRef.current) {
      const selected = dropdownRef.current.children[activeCursor] as HTMLElement;
      if (selected) {
        selected.focus();
      }
    }
  }, [activeCursor, showDropdown]);

  useEffect(() => {
    global.document.addEventListener('keydown', handleKeyPress);
    return () => {
      global.document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);

  return (
    <div className='written-submission__add-media__container' ref={buttonRef}>
      <WithTooltip
        enabled={!isMediaUploadSupported}
        focusable
        content='Media upload is only available in assignments.'
      >
        <Button
          className='gap-x1'
          color='secondary'
          variant='outlined'
          disabled={!isMediaUploadSupported}
          onClick={() => {
            setShowDropdown(!showDropdown);
          }}
          tabIndex={tabIndex}
        >
          <Text>{showTranslations ? addMediaText.button : 'Add Media'}</Text>
          <Icon icon='caret-down' />
        </Button>
      </WithTooltip>

      {isMediaUploadSupported && (
        <MediaDropdown
          setShowDropdown={setShowDropdown}
          showDropdown={showDropdown}
          dropdownRef={dropdownRef}
          activeCursor={activeCursor}
          showTranslation={showTranslations}
        />
      )}
    </div>
  );
};

interface MediaDropdownProps {
  setShowDropdown: (boolean) => void;
  showDropdown: boolean;
  dropdownRef: React.RefObject<HTMLDivElement | null>;
  activeCursor: number;
  showTranslation?: boolean;
}

const MediaDropdown = ({
  setShowDropdown,
  showDropdown,
  dropdownRef,
  activeCursor,
  showTranslation = false
}: MediaDropdownProps) => {
  const className = classnames(
    'written-submission__media-dropdown__container',
    showDropdown ? 'flex' : 'hidden'
  );

  return (
    <div className={className} ref={dropdownRef}>
      <ImageUploadButton
        showTranslation={showTranslation}
        setShowDropdown={setShowDropdown}
        tabIndex={activeCursor === 0 ? 0 : -1}
      />
      <AudioUploadButton
        showTranslation={showTranslation}
        setShowDropdown={setShowDropdown}
        tabIndex={activeCursor === 0 ? 0 : -1}
      />
    </div>
  );
};
